"use client";

/**
 * Recovery for tabs left open across a deploy.
 *
 * Every chunk filename is content-hashed and each container image ships only
 * its own, so a tab still running an earlier bundle requests filenames the
 * server no longer has. The browser reports that as a failed dynamic import,
 * which React turns into a render crash — the user just sees the error
 * boundary's blank page and has no way to know a reload would fix it.
 *
 * Reloading fetches the current index.html and the chunks it names, so do it
 * automatically instead of stranding them.
 */

const RELOAD_STATE_KEY = "simple-iep:stale-bundle-reload";
// Long enough that a genuinely broken deploy stops short of a reload loop,
// short enough that a *later* deploy in the same long-lived tab still heals.
const RELOAD_COOLDOWN_MS = 30_000;
const MAX_RELOADS = 3;

const staleBundlePattern =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|expected a javascript(-or-wasm)? module script|dynamically imported module/i;

const messageOf = (error: unknown): string => {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === "string") return error;
  return "";
};

export const isStaleBundleError = (error: unknown): boolean =>
  staleBundlePattern.test(messageOf(error));

type ReloadState = { at: number; count: number };

const readReloadState = (): ReloadState => {
  try {
    const raw = sessionStorage.getItem(RELOAD_STATE_KEY);
    if (raw == null) return { at: 0, count: 0 };
    const parsed = JSON.parse(raw) as Partial<ReloadState>;
    return {
      at: typeof parsed.at === "number" ? parsed.at : 0,
      count: typeof parsed.count === "number" ? parsed.count : 0,
    };
  } catch {
    // Private browsing and disabled storage both throw; treat as "never tried".
    return { at: 0, count: 0 };
  }
};

const writeReloadState = (state: ReloadState) => {
  try {
    sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify(state));
  } catch {
    // Nothing to do — worst case we reload once more than intended.
  }
};

/**
 * Reload if `error` looks like a chunk from a previous deploy.
 *
 * @returns whether a reload was triggered, so callers can skip reporting an
 * error that is about to be resolved by the reload.
 */
export const reloadForStaleBundle = (error: unknown): boolean => {
  if (!isStaleBundleError(error)) return false;

  const { at, count } = readReloadState();
  const now = Date.now();
  if (count >= MAX_RELOADS || now - at < RELOAD_COOLDOWN_MS) return false;

  writeReloadState({ at: now, count: count + 1 });
  console.warn("Bundle is from a previous deploy — reloading", error);
  window.location.reload();
  return true;
};

/**
 * Catch stale-bundle failures that never reach a React error boundary — route
 * preloads and other imports started outside of render.
 */
export const registerStaleBundleRecovery = () => {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadForStaleBundle((event as { payload?: unknown }).payload)) {
      event.preventDefault();
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (reloadForStaleBundle(event.reason)) {
      event.preventDefault();
    }
  });
};
